import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import type { MarkerData } from '../types/markers';
import './MapView.css'; // Import styles to ensure map-preview-* classes are available
import { HTTP_API_URL } from '../config/api';

interface UnmarkedImagesModalProps {
    markers: MarkerData[];
    onClose: () => void;
    onView: (marker: MarkerData) => void;
    onDelete: (marker: MarkerData) => void;
    onCreateMarker: (marker: MarkerData) => void;
}

const UnmarkedImagesModal: React.FC<UnmarkedImagesModalProps> = ({
    markers,
    onClose,
    onView,
    onDelete,
    onCreateMarker,
}) => {
    const [activeActionsForId, setActiveActionsForId] = useState<string | null>(null);

    const toggleActionsForMarker = (marker: MarkerData) => {
        setActiveActionsForId(prev => (prev === marker.id ? null : marker.id));
    };

    const handleThumbEnter = (marker: MarkerData) => {
        // Optional: prefetch or highlight logic
    };

    const handleThumbLeave = () => {
        // Optional
    };

    const resolveMarkerThumbnail = (marker: MarkerData) => {
        if (marker.thumbnail_url) {
            // If it starts with http, use it as is (signed URL)
            if (marker.thumbnail_url.startsWith('http')) {
                return marker.thumbnail_url;
            }
            // Otherwise treat as GCS path and use proxy
            const normalized = marker.thumbnail_url.startsWith('/')
                ? marker.thumbnail_url
                : `/gcs-images/${marker.thumbnail_url}`;
            return `${HTTP_API_URL}${normalized}`;
        }

        if (marker.thumbnail) {
            return `data:image/jpeg;base64,${marker.thumbnail}`;
        }

        // Fallback to full image if thumbnail missing
        if (marker.imageUrl) {
            return marker.imageUrl;
        }

        return null;
    };

    return createPortal(
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="map-preview-panel"
                onClick={(e) => e.stopPropagation()}
                style={{ margin: 'auto', position: 'relative', maxHeight: '80vh' }}
            >
                <div className="map-preview-header">
                    <span className="map-preview-chip">
                        <span className="notranslate">{markers.length}</span>&nbsp;unmarked images
                    </span>
                    <button className="map-preview-close" onClick={onClose}>×</button>
                </div>

                <div className="map-preview-grid" style={{ marginTop: '12px', overflowY: 'auto' }}>
                    {markers.map((marker) => {
                        const thumbSrc = resolveMarkerThumbnail(marker);
                        const isActive = activeActionsForId === marker.id;

                        return (
                            <div
                                key={marker.id}
                                className={`map-preview-thumb ${isActive ? 'is-active' : ''}`}
                                onClick={() => toggleActionsForMarker(marker)}
                                onMouseEnter={() => handleThumbEnter(marker)}
                                onMouseLeave={handleThumbLeave}
                            >
                                {thumbSrc ? (
                                    <img src={thumbSrc} alt={marker.filename} />
                                ) : (
                                    <div className="map-preview-thumb__placeholder" />
                                )}
                                <div className="map-preview-thumb__label">{marker.filename}</div>

                                <div className={`map-preview-actions ${isActive ? 'is-active' : ''}`}>
                                    <div className="map-preview-actions__buttons">
                                        <button
                                            type="button"
                                            className="map-preview-action"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onView(marker);
                                            }}
                                        >
                                            View
                                        </button>
                                        <button
                                            type="button"
                                            className="map-preview-action map-preview-action--danger"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(marker);
                                            }}
                                        >
                                            Delete
                                        </button>
                                        <button
                                            type="button"
                                            className="map-preview-action"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onCreateMarker(marker);
                                            }}
                                        >
                                            Create marker
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default UnmarkedImagesModal;
